/**
 * A revenda operando as organizações dos SEUS clientes finais (#1001).
 *
 * Superfície própria (`/reseller/customer-organizations`), separada do `organizationApi`
 * (superadmin) e do `invitationApi` (equipe da própria org): o backend confere a tenancy
 * pela org ATIVA (`X-Organization-Id`) e recusa — com código — o que a revenda não pode
 * fazer. Nunca mande `parent_id`/`type`: são derivados no servidor e a request inteira é
 * recusada (403 `campos_nao_aceitos` / `campos_nao_editaveis`) se vierem no corpo.
 */

import { apiClient } from './api'
import type { Invitation } from './invitationApi'

const BASE = '/api/v1/reseller/customer-organizations'

export interface CustomerOrgAssociation {
  id: string
  status: string
  approved_at?: string | null
}

/** Endereço estruturado (#1014) — a forma de ESCRITA. No backend vira as chaves que a cobrança lê. */
export interface EnderecoDoCliente {
  street: string
  number?: string | null
  complement?: string | null
  district?: string | null
  city: string
  state: string
  zip_code: string
  country?: string
}

/** A forma de LEITURA: o leitor devolve `null` no que o legado/parcial não tem. */
export interface EnderecoLido {
  street: string | null
  number: string | null
  complement: string | null
  district: string | null
  city: string | null
  state: string | null
  zip_code: string | null
  country: string | null
}

/** O que a API devolve sobre o cadastro — normalizado (só dígitos; UF maiúscula). */
export interface DadosCadastraisDoCliente {
  document: string | null
  document_type: 'cnpj' | 'cpf' | null
  email: string | null
  phone: string | null
  size: string | null
  industry: string | null
  address: EnderecoLido | null
}

export interface ResellerCustomerOrganization {
  id: string
  name: string
  type: 'end_customer'
  description?: string | null
  is_active: boolean
  parent_id: string | null
  frontend_url?: string | null
  created_at: string
  updated_at: string
  dados_cadastrais: DadosCadastraisDoCliente
  association: CustomerOrgAssociation | null
}

/** `GET /{id}`: a org + vínculo — alimenta o formulário de edição. */
export interface ResellerCustomerOrganizationDetail extends ResellerCustomerOrganization {
  captive: boolean
  association_status: string | null
}

/** O que a criação diz sobre a VPC: hoje só `not_created` (nada é criado automaticamente). */
export interface CustomerOrgVpcOutcome {
  status: 'not_created' | string
  message: string
}

export interface ResellerCustomerOrganizationCreated extends ResellerCustomerOrganization {
  vpc: CustomerOrgVpcOutcome
}

/**
 * O que a edição diz sobre o Asaas: `synced` (o customer lá foi atualizado), `not_registered`
 * (ainda não existe lá — nasce no primeiro pagamento, já com estes dados), `not_synced` (o
 * dado foi salvo aqui, mas o Asaas não foi atualizado — a cobrança ficou defasada) ou
 * `not_applicable` (nada mudou). Ausente na janela em que a API ainda é a anterior.
 */
export interface CustomerOrgAsaasOutcome {
  status: 'synced' | 'not_registered' | 'not_synced' | 'not_applicable' | string
  message?: string
}

export interface ResellerCustomerOrganizationUpdated extends ResellerCustomerOrganization {
  asaas?: CustomerOrgAsaasOutcome
}

/** Dados cadastrais que a revenda informa. Documento e e-mail são obrigatórios no cadastro. */
export interface DadosCadastraisDto {
  document?: string
  email?: string
  phone?: string | null
  size?: string | null
  industry?: string | null
  address?: EnderecoDoCliente | null
}

export interface CreateResellerCustomerOrgDto extends DadosCadastraisDto {
  name: string
  description?: string
  document: string
  email: string
}

export interface UpdateResellerCustomerOrgDto extends DadosCadastraisDto {
  name?: string
  description?: string | null
}

export interface InvitableRole {
  id: string
  name: string
  description?: string | null
}

/**
 * Um cliente que a revenda opera — os DOIS ramos da régua do backend: cativo (`parent_id` =
 * esta revenda) ou livre com associação ativa. É a faceta de ORGANIZAÇÃO; zonas e VPCs
 * (faceta SDN, `sdnResellerApi.listCustomersWithZones`) continuam só dos cativos.
 */
export interface ResellerCustomerListItem {
  id: string
  name: string
  description?: string | null
  is_active: boolean
  captive: boolean
  /** Status da `CustomerResellerAssociation` com ESTA revenda; `null` = sem associação. */
  association_status: string | null
  /** A revenda pode editar/convidar neste cliente — o backend recusa onde for `false`. */
  operable: boolean
  created_at: string
}

export const resellerCustomerOrgApi = {
  list: (): Promise<ResellerCustomerListItem[]> => apiClient.get(BASE),

  get: (customerId: string): Promise<ResellerCustomerOrganizationDetail> =>
    apiClient.get(`${BASE}/${customerId}`),

  create: (data: CreateResellerCustomerOrgDto): Promise<ResellerCustomerOrganizationCreated> =>
    apiClient.post(BASE, data),

  update: (
    customerId: string,
    data: UpdateResellerCustomerOrgDto,
  ): Promise<ResellerCustomerOrganizationUpdated> => apiClient.put(`${BASE}/${customerId}`, data),

  listInvitableRoles: (customerId: string): Promise<InvitableRole[]> =>
    apiClient.get(`${BASE}/${customerId}/invitable-roles`),

  listInvitations: (customerId: string): Promise<Invitation[]> =>
    apiClient.get(`${BASE}/${customerId}/invitations`),

  createInvitation: (
    customerId: string,
    data: { email: string; role_id: string },
  ): Promise<Invitation> => apiClient.post(`${BASE}/${customerId}/invitations`, data),

  cancelInvitation: (customerId: string, invitationId: string): Promise<void> =>
    apiClient.delete(`${BASE}/${customerId}/invitations/${invitationId}`),

  resendInvitation: (customerId: string, invitationId: string): Promise<Invitation> =>
    apiClient.post(`${BASE}/${customerId}/invitations/${invitationId}/resend`),
}
